import { compact, get, uniq } from 'lodash';
import { DEFAULT_ERROR } from 'utils/constants';
import DatadogHandler from '../utils/datadog';
import {
  onboardingService,
  userService,
  MS_PHASE_3_ENDPOINTS,
  centreSerivce,
  authService,
} from '../utils/msEndpointConstants';
import { retrieveObjectMapper, retrieveSortForMS } from '../utils';
import {
  getMe,
  getParentByUserId,
  getRolesByIds,
  getRoleTemplatesByRoleId,
  getUserAccessControls,
  getUserRoleRelationsByUserId,
} from './UserService';
import { getLevelByIds, getSchoolById } from './SchoolConfigService';
import { getCentreByIds, getProgramByIds } from './CentreService';
import {
  getChildByIds,
  getChildClassesByChildIds,
  getChildClosestLevelByChildIds,
  getChildEnrolmentStatusByChildIds,
  getChildRelationsByUserIds,
} from './ChildService';
import { getClassByIds } from './ClassService';
import { transformMeUserDetails } from '../utils/ms-utils/ms-phase-3/AuthUtils';
import {
  getRegChildParentsByParentIds,
  getRegistrationChildByIds,
  getRegParentByUserIds,
} from './RegistrationsService';

const fileName = 'src/services/AuthService.js';
const { CENTRE, AUTH } = MS_PHASE_3_ENDPOINTS;

export const msLogin = async (_, body) => {
  try {
    const resp = await authService().post(...AUTH.login(body));
    return {
      success: true,
      data: {
        login: get(resp, 'data.token', ''),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msLogin',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            get(error, 'response.data.error') ||
            get(error, 'message', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msFetchUserDetails = async () => {
  try {
    const resp = await getMe();
    const userId = get(resp, 'id');
    const [
      accessControls,
      roleRelations,
      parent,
      childRelationsResp,
      regParents,
    ] = await Promise.all([
      getUserAccessControls(userId),
      getUserRoleRelationsByUserId(userId),
      getParentByUserId(userId),
      getChildRelationsByUserIds(compact([userId])),
      getRegParentByUserIds([userId]),
    ]);
    const childRelations = get(childRelationsResp, `${userId}.data`) || [];
    const childIds = uniq(
      compact(childRelations?.map(child => child.fk_child))
    );
    const schoolId = get(
      (get(accessControls, 'data') || [])?.find(d => d.fk_school),
      'fk_school'
    );

    const roleIds = uniq(
      compact(get(roleRelations, 'data', [])?.map(d => d.fk_role))
    );
    const parentIds = uniq(compact(regParents || []).map(d => d.fk_parent));

    const [school, roles, roleTemplates, regChildParents] = await Promise.all([
      getSchoolById(schoolId),
      getRolesByIds(roleIds),
      Promise.all(
        roleIds.map(roleId =>
          getRoleTemplatesByRoleId(roleId).then(templates => ({
            [roleId]: templates,
          }))
        )
      ).then(res => res.reduce((acc, curr) => ({ ...acc, ...curr }), {})),
      getRegChildParentsByParentIds(parentIds),
    ]);

    const regChildIds = uniq(
      compact(
        regChildParents?.map(
          regChildParent => regChildParent?.fk_registration_child
        )
      )
    );

    const [
      children,
      childClasses,
      enrolmentStatuses,
      childClosestLevels,
      regChildren,
    ] = await Promise.all([
      getChildByIds(childIds),
      getChildClassesByChildIds({ childIds }),
      getChildEnrolmentStatusByChildIds(childIds),
      getChildClosestLevelByChildIds(childIds),
      getRegistrationChildByIds(regChildIds),
    ]);

    const centreIds = uniq([
      ...compact(get(accessControls, 'data', [])?.map(d => d.fk_centre)),
      ...compact(childClosestLevels?.map(d => d.fk_centre)),
    ]);

    const classIds = uniq(compact(childClasses?.map(d => d.fk_class)));
    const programIds = uniq([
      ...compact(childClasses?.map(d => d.fk_program)),
      ...compact(childClosestLevels?.map(d => d.fk_program)),
    ]);
    const levelIds = uniq(compact(childClosestLevels?.map(d => d.fk_level)));

    const [centres, classes, programs, levels] = await Promise.all([
      getCentreByIds(centreIds),
      getClassByIds(classIds),
      getProgramByIds(programIds),
      getLevelByIds(levelIds),
    ]);

    return transformMeUserDetails({
      resp,
      roleRelations,
      roles,
      roleTemplates,
      accessControls,
      school,
      centres,
      programs,
      levels,
      classes,
      childClasses,
      childClosestLevels,
      parent,
      childRelations,
      children,
      enrolmentStatuses,
      regParents,
      regChildParents,
      regChildren,
    });
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msLogin',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msLoginByLinkpass = async (_, reqData) => {
  try {
    const svc = userService();
    const resp = await svc.post(
      ...MS_PHASE_3_ENDPOINTS.LINKPASS.loginByLinkpass({
        link_pass_id_token: reqData?.linkPassIdToken,
      })
    );
    return {
      success: true,
      data: {
        loginByLinkPass: resp?.data.token,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msLoginByLinkpass',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msRegisterWithLinkPass = async (_, reqData) => {
  try {
    const svc = onboardingService();
    const resp = await svc.post(
      ...MS_PHASE_3_ENDPOINTS.LINKPASS.registerWithLinkpass({
        link_pass_id_token: reqData?.linkPassIdToken,
        token: reqData?.registerToken,
      })
    );

    return {
      success: true,
      data: {
        // add aditional user raw data, in case we need it later
        registerWithLinkPass: { ID: resp?.data?.id },
        user: resp?.data,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msRegisterWithLinkPass',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msLinkUserWithLinkPass = async (_, reqData) => {
  try {
    const svc = userService();
    const resp = await svc.post(
      ...MS_PHASE_3_ENDPOINTS.LINKPASS.linkUserWithLinkpass({
        email: reqData?.email,
        link_pass_id_token: reqData?.linkPassIdToken,
      })
    );
    const token = get(resp, 'data.token', '');
    return {
      success: true,
      data: {
        linkUserWithLinkPass: token,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msLinkUserWithLinkPass',
        fileName,
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchAccessibleCentreForModule = async (_, reqData) => {
  const { pagination } = reqData;
  const reqBody = {
    access_type_string: reqData?.accessType,
    include_closed_centres: reqData?.includeClosedCentres || false,
    ...(reqData?.module && {
      module: reqData?.module,
    }),
  };
  const params = {
    ...(pagination?.page && {
      page: pagination?.page,
    }),
    ...(pagination?.perPage && {
      perPage: pagination?.perPage,
    }),
    ...(pagination?.sort && retrieveSortForMS(pagination?.sort || [])),
  };
  try {
    const centreSvc = centreSerivce();
    const resp = await centreSvc.post(
      ...CENTRE.getAccessibleCentresForModule(reqBody, params)
    );
    return {
      success: true,
      data: {
        getAccessibleCentresForModule: {
          totalCount: resp?.totalCount || 0,
          data:
            resp?.data?.data?.map(item => ({
              ...retrieveObjectMapper(item, [
                'ID:id',
                'fk_school',
                'label',
                'code',
                'ecda_code',
                'cost_centre_code',
              ]),
            })) || [],
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        fileName,
        functionName: 'msFetchAccessibleCentreForModule',
        payload: { ...reqBody, params },
      },
      'error'
    );
    return {
      data: undefined,
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msForgotPassword = async (_, reqData) => {
  try {
    const resp = await authService().post(
      ...AUTH.forgotPassword(get(reqData, 'email', ''))
    );
    return {
      success: true,
      data: get(resp, 'data.success', false),
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msForgetPassword',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            get(error, 'response.data.error') ||
            get(error, 'message', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msGetSsoURL = async () => {
  try {
    const resp = await authService().get(AUTH.getSsoUrl);
    return {
      success: true,
      data: {
        getLoginUrl: get(resp, 'data.url', ''),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetSsoURL',
        fileName,
      },
      'error'
    );
    throw error;
  }
};

export const msValidateOtp = async (_, reqData) => {
  try {
    const resp = await authService().post(
      ...AUTH.validateOtp({
        otp_received: reqData?.otpReceived,
        user_id: reqData?.userId,
      })
    );
    return {
      success: true,
      data: resp?.data,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msValidateOtp',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            get(error, 'response.data.error') ||
            get(error, 'response.data.message') ||
            get(error, 'message', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msResetPassword = async (_, reqData) => {
  try {
    if (!reqData?.password || !reqData?.tokenString)
      return { success: true, data: {} };

    const resp = await authService().post(
      ...AUTH.resetPassword({
        password: reqData?.password,
        token: reqData?.tokenString,
      })
    );

    return {
      success: true,
      data: {
        resetPassword: resp?.data?.success || false,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msResetPassword',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            get(error, 'response.data.error') ||
            get(error, 'message', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const msLoginForEpToken = async (_, { token }) => {
  try {
    if (!token) return { success: true, data: {} };

    const resp = await Promise.resolve();
    return {
      success: true,
      data: {
        loginEP: get(resp, 'data.token', ''),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msLoginForEpToken',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            get(error, 'response.data.error') ||
            get(error, 'message', DEFAULT_ERROR),
        },
      ],
    };
  }
};
